import { useState, type FormEvent } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import { createSetlist } from '../../hooks/useSetlists';

interface Props {
  bandId: string;
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface SongInput {
  title: string;
  artist: string;
  duration_seconds: number | null;
  notes: string;
}

export default function CreateSetlistModal({ bandId, userId, onClose, onSuccess }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [songs, setSongs] = useState<SongInput[]>([{ title: '', artist: '', duration_seconds: null, notes: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function addSong() {
    setSongs([...songs, { title: '', artist: '', duration_seconds: null, notes: '' }]);
  }

  function removeSong(index: number) {
    setSongs(songs.filter((_, i) => i !== index));
  }

  function updateSong(index: number, field: keyof SongInput, value: string | number | null) {
    const updated = [...songs];
    updated[index] = { ...updated[index], [field]: value };
    setSongs(updated);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const validSongs = songs.filter((s) => s.title.trim());

    const { error } = await createSetlist(bandId, title, description, isPublic, validSongs, userId);
    if (error) {
      setError(error);
      setLoading(false);
    } else {
      onSuccess();
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
          <h2 className="text-lg font-semibold">Create Setlist</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Setlist Name *</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Friday Night Set, Greatest Hits"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="rounded border-gray-700 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-300">Make this setlist public</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Songs</label>
            <div className="space-y-2">
              {songs.map((song, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-500 text-sm w-6">{idx + 1}.</span>
                  <input
                    type="text"
                    value={song.title}
                    onChange={(e) => updateSong(idx, 'title', e.target.value)}
                    placeholder="Song title"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    value={song.artist}
                    onChange={(e) => updateSong(idx, 'artist', e.target.value)}
                    placeholder="Artist"
                    className="w-32 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {songs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSong(idx)}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSong}
              className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-sm mt-2"
            >
              <Plus className="w-4 h-4" />
              Add Song
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
            >
              {loading ? 'Creating...' : 'Create Setlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
